import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Search, Filter, ChevronDown, ChevronUp, ArrowUpDown,
  ExternalLink, Globe, MapPin, Building2, CheckSquare, Square,
  ArrowRight, X, Hotel, Waves, Mountain, Sparkles,
} from "lucide-react";
import HotelIntelligencePanel from "../components/HotelIntelligencePanel";

const FACILITY_LABELS = { spa: "Spa", golf: "Golf", beach: "Beach", ski: "Ski", mice_capable: "MICE", meeting_rooms: "Meetings", outdoor: "Outdoor", daylight: "Daylight", wedding: "Wedding", pool: "Pool", fitness: "Fitness" };

function ensureUrl(url) {
  if (!url) return null;
  const s = url.trim();
  if (!s) return null;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  return "https://" + s;
}

export default function DatabasePage() {
  const { token, user } = useAuth();
  const [hotels, setHotels] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ region: "", country: "", city: "", consortium: "", import_batch: "", hotel_category: "", spa: false, golf: false, beach: false, ski: false, mice_capable: false, meeting_rooms: false, outdoor: false, daylight: false, wedding: false });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [pushing, setPushing] = useState({});
  const [bulkPushing, setBulkPushing] = useState(false);
  const [expandedHotel, setExpandedHotel] = useState(null);
  const [hotelContacts, setHotelContacts] = useState({});
  const limit = 100;
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 300);
  }, [search]);

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (filters.region) params.set("region", filters.region);
      if (filters.country) params.set("country", filters.country);
      if (filters.city) params.set("city", filters.city);
      if (filters.consortium) params.set("consortium", filters.consortium);
      if (filters.import_batch) params.set("import_batch", filters.import_batch);
      if (filters.hotel_category) params.set("hotel_category", filters.hotel_category);
      if (filters.spa) params.set("spa", "true");
      if (filters.golf) params.set("golf", "true");
      if (filters.beach) params.set("beach", "true");
      if (filters.ski) params.set("ski", "true");
      if (filters.mice_capable) params.set("mice_capable", "true");
      if (filters.meeting_rooms) params.set("meeting_rooms", "true");
      if (filters.outdoor) params.set("outdoor", "true");
      if (filters.daylight) params.set("daylight", "true");
      if (filters.wedding) params.set("wedding", "true");
      params.set("sort_by", sortBy);
      params.set("sort_dir", sortDir);
      params.set("skip", String(page * limit));
      params.set("limit", String(limit));
      const res = await axios.get(`${API}/hotels/database?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setHotels(res.data.hotels);
      setTotal(res.data.total);
    } catch { toast.error("Failed to load hotels"); }
    setLoading(false);
  }, [token, debouncedSearch, filters, sortBy, sortDir, page]);

  useEffect(() => { fetchHotels(); }, [fetchHotels]);

  useEffect(() => {
    axios.get(`${API}/hotels/database/filter-options`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFilterOptions(r.data)).catch(() => {});
  }, [token]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const fetchHotelContacts = async (hotelId) => {
    try {
      const r = await axios.get(`${API}/hotels/database/${hotelId}/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      setHotelContacts(prev => ({ ...prev, [hotelId]: r.data }));
    } catch { /* ignore */ }
  };

  const toggleExpand = (hotelId) => {
    if (expandedHotel === hotelId) {
      setExpandedHotel(null);
    } else {
      setExpandedHotel(hotelId);
      if (!hotelContacts[hotelId]) fetchHotelContacts(hotelId);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === hotels.length) setSelected(new Set());
    else setSelected(new Set(hotels.map(h => h.id)));
  };

  const pushToPipeline = async (hotelId) => {
    setPushing(p => ({ ...p, [hotelId]: true }));
    try {
      await axios.post(`${API}/hotels/database/${hotelId}/push-to-pipeline`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Hotel pushed to Pipeline (Discover)");
      fetchHotels();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed";
      toast.error(msg);
    }
    setPushing(p => ({ ...p, [hotelId]: false }));
  };

  const bulkPush = async () => {
    if (selected.size === 0) return;
    setBulkPushing(true);
    try {
      const res = await axios.post(`${API}/hotels/database/bulk-push-to-pipeline`, { hotel_ids: [...selected] }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.pushed} hotels pushed to Pipeline, ${res.data.skipped} skipped`);
      setSelected(new Set());
      fetchHotels();
    } catch { toast.error("Bulk push failed"); }
    setBulkPushing(false);
  };

  const activeFilterCount = Object.entries(filters).filter(([, v]) => typeof v === "boolean" ? v : v !== "").length;
  const selectCls = "bg-white/[0.04] border border-white/[0.08] text-white text-xs rounded-lg px-2.5 py-1.5 outline-none [&>option]:bg-[#0A0A0A]";
  const toggleCls = (on) => `px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all cursor-pointer ${on ? "bg-cyan-500/15 border-cyan-500/30 text-cyan-400" : "bg-white/[0.02] border-white/[0.06] text-white/25 hover:text-white/40"}`;
  const thCls = "text-left text-white/30 text-[11px] uppercase tracking-wider font-semibold px-3 py-2.5 border-b border-white/[0.06] cursor-pointer hover:text-white/50 select-none";
  const SortIcon = ({ col }) => sortBy === col ? (sortDir === "asc" ? <ChevronUp className="w-3 h-3 inline ml-0.5" /> : <ChevronDown className="w-3 h-3 inline ml-0.5" />) : null;

  return (
    <div className="p-4 lg:p-6" data-testid="database-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-white" data-testid="database-title">Hotel Database</h1>
          <p className="text-white/40 text-sm mt-0.5">{total} hotels in master database</p>
        </div>
        {selected.size > 0 && (
          <button
            onClick={bulkPush}
            disabled={bulkPushing}
            className="flex items-center gap-2 px-4 py-2 bg-[#C9A96E] text-white rounded-xl text-sm font-semibold hover:bg-[#C9A96E]/90 disabled:opacity-40 transition-all"
            data-testid="bulk-push-btn"
          >
            {bulkPushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Push {selected.size} to Pipeline
          </button>
        )}
      </div>

      {/* Search & Filters */}
      <div className="mb-4 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search hotels..."
              className="w-full pl-9 pr-3 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white text-sm outline-none placeholder:text-white/20 focus:border-[#C9A96E]/30"
              data-testid="database-search"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${activeFilterCount > 0 ? "bg-[#C9A96E]/10 border-[#C9A96E]/20 text-[#C9A96E]" : "bg-white/[0.03] border-white/[0.06] text-white/30 hover:text-white/50"}`}
            data-testid="database-toggle-filters"
          >
            <Filter className="w-3.5 h-3.5" />
            Filters {activeFilterCount > 0 && <span className="bg-[#C9A96E] text-white w-4 h-4 rounded-full text-[9px] flex items-center justify-center">{activeFilterCount}</span>}
          </button>
        </div>

        {showFilters && filterOptions && (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              <select value={filters.region} onChange={e => { setFilters(f => ({ ...f, region: e.target.value })); setPage(0); }} className={selectCls} data-testid="db-filter-region">
                <option value="">All Regions</option>
                {(filterOptions.regions || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={filters.country} onChange={e => { setFilters(f => ({ ...f, country: e.target.value })); setPage(0); }} className={selectCls} data-testid="db-filter-country">
                <option value="">All Countries</option>
                {(filterOptions.countries || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={filters.city} onChange={e => { setFilters(f => ({ ...f, city: e.target.value })); setPage(0); }} className={selectCls} data-testid="db-filter-city">
                <option value="">All Cities</option>
                {(filterOptions.cities || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={filters.consortium} onChange={e => { setFilters(f => ({ ...f, consortium: e.target.value })); setPage(0); }} className={selectCls} data-testid="db-filter-consortium">
                <option value="">All Consortiums</option>
                {(filterOptions.consortiums || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={filters.import_batch} onChange={e => { setFilters(f => ({ ...f, import_batch: e.target.value })); setPage(0); }} className={selectCls} data-testid="db-filter-batch">
                <option value="">All Batches</option>
                {(filterOptions.import_batches || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={filters.hotel_category} onChange={e => { setFilters(f => ({ ...f, hotel_category: e.target.value })); setPage(0); }} className={selectCls} data-testid="db-filter-category">
                <option value="">All Categories</option>
                {(filterOptions.hotel_categories || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(FACILITY_LABELS).map(([key, label]) => {
                const count = filterOptions.facility_counts?.[key];
                return (
                  <button key={key} onClick={() => { setFilters(f => ({ ...f, [key]: !f[key] })); setPage(0); }} className={toggleCls(filters[key])} data-testid={`db-filter-${key}`}>
                    {label}{count > 0 && <span className="ml-1 opacity-50">({count})</span>}
                  </button>
                );
              })}
            </div>
            {activeFilterCount > 0 && (
              <button onClick={() => { setFilters({ region: "", country: "", city: "", consortium: "", import_batch: "", hotel_category: "", spa: false, golf: false, beach: false, ski: false, mice_capable: false, meeting_rooms: false, outdoor: false, daylight: false, wedding: false }); setPage(0); }} className="text-white/20 text-xs hover:text-white/40" data-testid="db-clear-filters">Clear all filters</button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]" data-testid="database-table">
            <thead>
              <tr>
                <th className="w-10 px-3 py-2.5 border-b border-white/[0.06]">
                  <button onClick={toggleAll} className="text-white/20 hover:text-white/50" data-testid="select-all-btn">
                    {selected.size === hotels.length && hotels.length > 0 ? <CheckSquare className="w-4 h-4 text-[#C9A96E]" /> : <Square className="w-4 h-4" />}
                  </button>
                </th>
                <th onClick={() => toggleSort("hotel_name")} className={thCls} data-testid="sort-name">Hotel <SortIcon col="hotel_name" /></th>
                <th onClick={() => toggleSort("city")} className={thCls}>City <SortIcon col="city" /></th>
                <th onClick={() => toggleSort("country")} className={thCls}>Country <SortIcon col="country" /></th>
                <th onClick={() => toggleSort("region")} className={thCls}>Region <SortIcon col="region" /></th>
                <th onClick={() => toggleSort("consortium")} className={thCls}>Consortium <SortIcon col="consortium" /></th>
                <th className={`${thCls} cursor-default`}>Facilities</th>
                <th className={`${thCls} cursor-default`}>Pipeline</th>
                <th className={`${thCls} cursor-default`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && hotels.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C9A96E] mx-auto" /></td></tr>
              ) : hotels.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-16 text-white/20 text-sm">No hotels found. Import some data first.</td></tr>
              ) : hotels.map(h => {
                const facilities = Object.keys(FACILITY_LABELS).filter(f => h[f]);
                const inPipeline = !!h.linked_deal_id;
                const isExpanded = expandedHotel === h.id;
                return (
                  <React.Fragment key={h.id}>
                  <tr className={`border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors cursor-pointer ${selected.has(h.id) ? "bg-[#C9A96E]/[0.03]" : ""}`} data-testid={`hotel-row-${h.id}`} onClick={() => toggleExpand(h.id)}>
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <button onClick={() => toggleSelect(h.id)} className="text-white/20 hover:text-white/50">
                        {selected.has(h.id) ? <CheckSquare className="w-4 h-4 text-[#C9A96E]" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <Hotel className="w-4 h-4 text-white/15 shrink-0" />
                        <span className="text-white text-sm font-medium">{h.hotel_name}</span>
                      </div>
                      {h.primary_website && (
                        <a href={ensureUrl(h.primary_website)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-white/25 text-[11px] mt-0.5 ml-6 hover:text-[#C9A96E]/60" onClick={e => e.stopPropagation()}>
                          <Globe className="w-3 h-3" />{h.primary_website.replace(/^https?:\/\/(www\.)?/, '').slice(0, 30)}
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-white/50 text-sm">{h.city}</td>
                    <td className="px-3 py-2.5 text-white/50 text-sm">{h.country}</td>
                    <td className="px-3 py-2.5 text-white/40 text-sm">{h.region}</td>
                    <td className="px-3 py-2.5 text-white/40 text-sm">{h.consortium}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-0.5">
                        {facilities.map(f => (
                          <span key={f} className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400/70 font-medium">{FACILITY_LABELS[f]}</span>
                        ))}
                        {h.rooms && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.04] text-white/25 font-mono">{h.rooms}rm</span>}
                        {h.group_size && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400/50 font-mono">grp {h.group_size}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {inPipeline ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400/70 font-medium" data-testid={`pipeline-badge-${h.id}`}>In Pipeline</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] text-white/20 font-medium">Not Active</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      {!inPipeline && (
                        <button
                          onClick={() => pushToPipeline(h.id)}
                          disabled={pushing[h.id]}
                          className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg bg-[#C9A96E]/10 text-[#C9A96E] hover:bg-[#C9A96E]/20 transition-colors font-medium disabled:opacity-30"
                          data-testid={`push-btn-${h.id}`}
                        >
                          {pushing[h.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                          Pipeline
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* Expanded Detail Row */}
                  {isExpanded && (
                    <tr className="border-b border-white/[0.06] bg-white/[0.015]" data-testid={`hotel-detail-${h.id}`}>
                      <td colSpan={9} className="px-4 py-4">
                        {/* Top row: Identity + Contacts side by side */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                          {/* Identity */}
                          <div className="space-y-1.5 text-xs" data-testid="hotel-identity">
                            <p className="text-white/25 text-[10px] uppercase tracking-wider font-semibold mb-1">Identitt</p>
                            {h.consortium && <div className="flex justify-between"><span className="text-white/30">Konsortium</span><span className="text-amber-400/70 font-medium">{h.consortium}</span></div>}
                            {h.hotel_category && <div className="flex justify-between"><span className="text-white/30">Kategorie</span><span className="text-white/60">{h.hotel_category}</span></div>}
                            {h.location_type && <div className="flex justify-between"><span className="text-white/30">Lagetyp</span><span className="text-white/60">{h.location_type}</span></div>}
                            {h.seasonality && <div className="flex justify-between"><span className="text-white/30">Saison</span><span className="text-white/60">{h.seasonality}</span></div>}
                            {h.import_batch && <div className="flex justify-between"><span className="text-white/30">Import Batch</span><span className="text-white/40 font-mono text-[11px]">{h.import_batch}</span></div>}
                            {(h.primary_website || h.derived_website) && (
                              <div className="mt-1">
                                <div className="flex items-center gap-1 mt-0.5">
                                  <a href={ensureUrl(h.primary_website || h.derived_website)} target="_blank" rel="noopener noreferrer" className="text-blue-400/60 hover:text-blue-400/80 text-[11px] truncate max-w-[200px]">{(h.primary_website || h.derived_domain || "").replace("https://","")}</a>
                                  {h.website_source && <span className={`text-[8px] px-1 py-0.5 rounded shrink-0 ${h.website_source === "confirmed" ? "bg-green-500/10 text-green-400/60" : h.website_source === "derived_from_email" ? "bg-amber-500/10 text-amber-400/60" : h.website_source === "imported" ? "bg-blue-500/10 text-blue-400/60" : h.website_source === "ai_discovered" ? "bg-purple-500/10 text-purple-400/60" : "bg-white/5 text-white/30"}`}>{h.website_source.replace(/_/g, " ")}</span>}
                                </div>
                              </div>
                            )}
                            <div className="flex justify-between"><span className="text-white/30">Pipeline</span>{h.linked_deal_id ? <span className="text-green-400/70 text-[10px] font-medium">Aktiv</span> : <span className="text-white/20 text-[10px]">Noch nicht</span>}</div>
                          </div>

                          {/* MICE Profile */}
                          <div className="space-y-1.5 text-xs" data-testid="hotel-mice">
                            {(() => {
                              const mice = (h.source_profiles || {}).mice;
                              if (!mice) return (
                                <div>
                                  <p className="text-white/25 text-[10px] uppercase tracking-wider font-semibold mb-1">MICE & Kapazitt</p>
                                  <p className="text-white/15 text-[10px]">Kein MICE-Profil vorhanden</p>
                                  {h.rooms != null && <div className="flex justify-between mt-2"><span className="text-white/30">Zimmer gesamt</span><span className="text-white/60 font-mono">{h.rooms}</span></div>}
                                </div>
                              );
                              const miceData = [
                                { key: "rooms", label: "Zimmer" }, { key: "guest_rooms", label: "Gstezimmer" },
                                { key: "suites", label: "Suiten & Villen" }, { key: "meeting_rooms", label: "Meeting-Rume", bool: true },
                                { key: "group_size", label: "Max Gruppe" }, { key: "banquet", label: "Bankett" },
                                { key: "sq_meters", label: "Meetingflche (qm)" }, { key: "largest_meeting_room", label: "Grter Raum (qm)" },
                                { key: "theatre_capacity", label: "Theater" }, { key: "classroom_capacity", label: "Classroom" },
                                { key: "restaurants", label: "Restaurants" }, { key: "wireless_internet", label: "WiFi", bool: true },
                              ];
                              return (
                                <div data-testid="mice-profile-section">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-[#C9A96E]/60 text-[10px] uppercase tracking-wider font-semibold">MICE Profil</p>
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#C9A96E]/10 text-[#C9A96E]/50">{mice.source_name}</span>
                                  </div>
                                  {miceData.filter(f => mice[f.key] != null).map(f => (
                                    <div key={f.key} className="flex justify-between">
                                      <span className="text-white/30">{f.label}</span>
                                      <span className="text-white/60 font-mono">{f.bool ? (mice[f.key] ? "Ja" : "Nein") : mice[f.key]}</span>
                                    </div>
                                  ))}
                                  {mice.batch_name && <p className="text-white/15 text-[9px] mt-1 font-mono">batch: {mice.batch_name}</p>}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Contacts */}
                          <div className="space-y-1.5 text-xs" data-testid={`hotel-contacts-col-${h.id}`}>
                            <p className="text-white/25 text-[10px] uppercase tracking-wider font-semibold mb-1">Kontakte</p>
                            {(hotelContacts[h.id] || []).length > 0 ? (
                              <div className="space-y-1.5">
                                {hotelContacts[h.id].map(c => (
                                  <div key={c.id} className="p-2 rounded-lg bg-green-500/[0.03] border border-green-500/10">
                                    <p className="text-white text-[11px] font-medium">{c.full_name}</p>
                                    {(c.role || c.title) && <p className="text-white/40 text-[10px]">{[c.title, c.role].filter(Boolean).join(" · ")}</p>}
                                    {c.email && <p className="text-white/30 text-[10px] truncate">{c.email}</p>}
                                    {c.phone && <p className="text-white/30 text-[10px]">{c.phone}</p>}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-white/15 text-[10px]">Keine Kontakte verknpft</p>
                            )}
                          </div>
                        </div>

                        {/* AI Intelligence Panel - Full Width */}
                        <div onClick={e => e.stopPropagation()}>
                          <HotelIntelligencePanel hotel={h} token={token} onRefresh={fetchHotels} />
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
            <span className="text-white/25 text-xs">{page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="text-white/30 text-xs px-2 py-1 rounded border border-white/[0.06] hover:text-white/50 disabled:opacity-20">Prev</button>
              <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total} className="text-white/30 text-xs px-2 py-1 rounded border border-white/[0.06] hover:text-white/50 disabled:opacity-20">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
